/**
 * Sidebar layout persistence + pure reorder rules.
 *
 * The sidebar is ONE ordered list of "blocks":
 *   - a loose tab  -> the tab id itself (e.g. "roomrack")
 *   - a menu group -> its name, prefixed with GROUP_BLOCK_PREFIX
 *
 * Block ORDER lives in localStorage (per user, per device) because it is
 * presentation-only. Group MEMBERSHIP lives in the database
 * (users.menu_groups) because the preferences modal also edits it, so a
 * membership change must survive on every device.
 *
 * Every reorder rule below is a pure function so it can be unit-tested
 * without a DOM.
 */

export const TAB_ORDER_STORAGE_PREFIX = "adapthub_sidebar_tab_order_";

/** Prefix that marks a block id as a menu group instead of a tab. */
export const GROUP_BLOCK_PREFIX = "group:";

/** localStorage key holding one user's block order. */
export function getTabOrderStorageKey(userId?: string): string {
  return `${TAB_ORDER_STORAGE_PREFIX}${userId || "default"}`;
}

/** Build the unified-order block id for a menu group. */
export function groupBlockId(name: string): string {
  return `${GROUP_BLOCK_PREFIX}${name}`;
}

/** True when a block id refers to a menu group rather than a tab. */
export function isGroupBlock(blockId: string): boolean {
  return blockId.startsWith(GROUP_BLOCK_PREFIX);
}

/** Extract the group name from a group block id. */
export function groupNameFromBlock(blockId: string): string {
  return blockId.slice(GROUP_BLOCK_PREFIX.length);
}

/** Read the saved block order. Returns [] when absent or corrupt. */
export function loadSavedTabOrder(userId?: string): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(getTabOrderStorageKey(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.every((id) => typeof id === "string")) {
      return parsed;
    }
  } catch {
    // Graceful fallback on corrupt storage
  }
  return [];
}

/** Persist the block order. Silently ignores quota/privacy failures. */
export function saveTabOrder(order: string[], userId?: string): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(getTabOrderStorageKey(userId), JSON.stringify(order));
  } catch {
    // Storage quota or privacy restriction
  }
}

/** Drop the saved block order (back to the default arrangement). */
export function removeSavedTabOrder(userId?: string): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(getTabOrderStorageKey(userId));
  } catch {
    // Ignore
  }
}

/**
 * Resolve the order actually rendered.
 *
 * The saved order wins for blocks it knows about; blocks added since the order
 * was saved (a new tab, a newly created group) are appended in their default
 * position, and blocks that no longer exist are dropped. This keeps a stale
 * localStorage entry from hiding anything.
 */
export function resolveBlockOrder(
  defaultBlockIds: string[],
  savedOrder: string[],
): string[] {
  if (!savedOrder.length) return [...defaultBlockIds];

  const known = new Set(defaultBlockIds);
  const ordered = savedOrder.filter((id) => known.has(id));
  const seen = new Set(ordered);
  for (const id of defaultBlockIds) {
    if (!seen.has(id)) ordered.push(id);
  }
  return ordered;
}

/** Where a dragged block lands relative to the block it was dropped on. */
export type DropPosition = "before" | "after";

/**
 * Move `sourceId` next to `targetId`.
 *
 * Returns the input order unchanged when the move is invalid (same id, or an
 * id that is not part of the order) so callers never persist a broken layout.
 */
export function moveBlock(
  order: string[],
  sourceId: string,
  targetId: string,
  position: DropPosition,
): string[] {
  if (sourceId === targetId) return order;
  if (!order.includes(sourceId) || !order.includes(targetId)) return order;

  const next = order.filter((id) => id !== sourceId);
  const targetIndex = next.indexOf(targetId);
  if (targetIndex === -1) return order;

  next.splice(position === "before" ? targetIndex : targetIndex + 1, 0, sourceId);
  return next;
}

/** Minimal shape of a menu group needed by the membership helpers. */
export interface GroupLike {
  name: string;
  items: string[];
}

/**
 * Move `tabId` INTO `groupName`, appending it to that group's items and
 * removing it from every other group.
 *
 * Returns a NEW groups array, or null when nothing would change (the tab is
 * already the last member of that group and belongs to no other) so callers
 * can skip the database write entirely.
 */
export function addTabToGroup<T extends GroupLike>(
  groups: T[],
  tabId: string,
  groupName: string,
): T[] | null {
  const target = groups.find((g) => g.name === groupName);
  if (!target) return null;

  const isLastMember = target.items[target.items.length - 1] === tabId;
  const inOtherGroup = groups.some(
    (g) => g.name !== groupName && g.items.includes(tabId),
  );
  if (isLastMember && !inOtherGroup) return null;

  return groups.map((g) => {
    if (g.name === groupName) {
      return { ...g, items: [...g.items.filter((id) => id !== tabId), tabId] };
    }
    if (g.items.includes(tabId)) {
      return { ...g, items: g.items.filter((id) => id !== tabId) };
    }
    return g;
  });
}

/**
 * Remove `tabId` from whichever group holds it, turning it back into a loose
 * tab. Returns a NEW groups array, or null when the tab is in no group.
 */
export function removeTabFromGroup<T extends GroupLike>(
  groups: T[],
  tabId: string,
): T[] | null {
  if (!groups.some((g) => g.items.includes(tabId))) return null;
  return groups.map((g) =>
    g.items.includes(tabId)
      ? { ...g, items: g.items.filter((id) => id !== tabId) }
      : g,
  );
}
